// Город из 3D-сцены Blender (assets/city-v2): одна прокручиваемая картина на расу — гавань, замок за стеной и
// поля снаружи. Слои: фон → задняя часть стены → здания по глубине → передняя часть стены → подписи.
// Каждое здание — отдельная картинка нужной ступени (s1: 1–9, s2: 10–19, s3: 20+), поставленная ровно в свой
// участок (координаты из того же рендера), поэтому здания не «плавают» и не перекрывают друг друга.
import type { BuildingView } from '@/api/types';
import { CASTLE_PLOT_TYPES, FIELD_PLOT_TYPES } from '@sk/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { gameHour } from '@/lib/gameClock';
import { daylightAt } from '@sk/shared';
import { type CityFx, drawConstruction, drawGulls, drawLevelUp, drawNight, drawSmoke, drawTorches, drawWalker, makeWalkers, walkerAt } from './cityLife';
import type { TownTarget } from './TownScene';

export interface CityLayout {
  w: number;
  h: number;
  lots: Record<string, { x: number; y: number; depth: number; hit: number[][] }>;
  sprites: Record<string, { file: string; x: number; y: number; w: number; h: number; fx?: CityFx }>;
  wall: { tiers: { back: string | null; front: string | null; fx_back?: CityFx; fx_front?: CityFx }[] };
  /** Факелы и т.п. фона (координаты картины) */
  fx?: CityFx;
  gates?: Record<string, number[]>;
  streets?: number[][][];
  /** Леса стройки сняты на участке anchor — на другом участке сдвигаются на разницу координат */
  scaffolds?: { anchor_lot: string; anchor: number[] };
}

const BASE = '/art/city';
const layoutCache = new Map<string, Promise<CityLayout | null>>();

/** Есть ли 3D-город для расы (иначе — прежний рисованный город). */
/** 3D-город пока в разработке: включается ?city3d=1 (запоминается), выключается ?city3d=0. */
function city3dEnabled(): boolean {
  try {
    const q = new URLSearchParams(window.location.search).get('city3d');
    if (q === '1' || q === '0') localStorage.setItem('sk_city3d', q);
    return localStorage.getItem('sk_city3d') === '1';
  } catch {
    return false;
  }
}

export function loadCityLayout(race: string): Promise<CityLayout | null> {
  if (!city3dEnabled()) return Promise.resolve(null);
  let p = layoutCache.get(race);
  if (!p) {
    p = fetch(`${BASE}/${race}/layout.json`)
      .then((r) => (r.ok ? (r.json() as Promise<CityLayout>) : null))
      .catch(() => null);
    layoutCache.set(race, p);
  }
  return p;
}

const images = new Map<string, HTMLImageElement>();
function image(url: string, onLoad: () => void): HTMLImageElement {
  let im = images.get(url);
  if (!im) {
    im = new Image();
    im.decoding = 'async';
    im.src = url;
    images.set(url, im);
  }
  if (!im.complete) im.addEventListener('load', onLoad, { once: true });
  return im;
}

/** Хук: есть ли 3D-город для расы (тогда поля внутри сцены и отдельная вкладка «Поля» не нужна). */
export function useHasCity3D(race: string | null | undefined): boolean {
  const [has, setHas] = useState(false);
  useEffect(() => {
    if (!race) {
      setHas(false);
      return;
    }
    let alive = true;
    void loadCityLayout(race).then((l) => {
      if (alive) setHas(!!l);
    });
    return () => {
      alive = false;
    };
  }, [race]);
  return has;
}

/** Ступень картинки здания по уровню. */
export function cityStage(level: number): 1 | 2 | 3 {
  return level >= 20 ? 3 : level >= 10 ? 2 : 1;
}
/** Вид стены по уровню: 0 — нет, 1 — частокол, 2 — каменная, 3 — крепостная. */
export function wallTier(level: number): 0 | 1 | 2 | 3 {
  return level <= 0 ? 0 : level < 5 ? 1 : level < 15 ? 2 : 3;
}

function inPolygon(poly: number[][], x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i] as [number, number];
    const [xj, yj] = poly[j] as [number, number];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export type CityTarget = TownTarget & { zone?: 'castle' | 'field' };

export function City3D({
  race,
  layout,
  buildings,
  selected,
  onSelect,
  wallLevel,
  buildingLabels,
  guide = null,
  label,
}: {
  race: string;
  layout: CityLayout;
  buildings: BuildingView[];
  selected: TownTarget | null;
  onSelect: (t: CityTarget | null) => void;
  wallLevel: number;
  buildingLabels: Record<string, string>;
  guide?: string | null;
  label: string;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const cam = useRef({ x: layout.w / 2, y: layout.h * 0.55, zoom: 0.5 });
  const [, redraw] = useState(0);
  const dirty = useRef(true);
  const url = (file: string) => `${BASE}/${race}/${file}`;
  // Новые данные (здания, выбор, стена) — перерисовать
  dirty.current = true;

  // Прохожие по улицам; при «уменьшить движение» — без постоянной анимации
  const walkers = useMemo(() => makeWalkers(layout.streets), [layout]);
  const still = useMemo(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  const byType = new Map<string, BuildingView>();
  for (const b of buildings) if (!byType.has(b.type)) byType.set(b.type, b);
  // Завершённые улучшения (уровень вырос) — короткая вспышка над участком
  const levels = useRef(new Map<string, number>());
  const levelUps = useRef(new Map<string, number>());
  useEffect(() => {
    const now = performance.now() / 1000;
    for (const b of buildings) {
      const prev = levels.current.get(b.type);
      if (prev != null && b.level > prev) levelUps.current.set(b.type, now);
      levels.current.set(b.type, b.level);
    }
  }, [buildings]);

  // Перерисовка по требованию + анимация лесов стройки
  useEffect(() => {
    let raf = 0;
    let last = 0;
    const frame = (t: number) => {
      raf = requestAnimationFrame(frame);
      const building = buildings.some((b) => b.upgradingTo != null);
      // Жизнь города (факелы, дым, прохожие) — ~20 кадров/с, только когда вкладка видна
      const life = !still && document.visibilityState === 'visible' && t - last > 50;
      if (!dirty.current && !life && !(building && t - last > 120)) return;
      last = t;
      dirty.current = false;
      draw(t / 1000);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  });

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      fit();
      dirty.current = true;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fit = () => {
    const el = wrap.current;
    if (!el) return;
    // Картина шире экрана телефона в ~1.6 раза: по ширине умещается почти вся, вертикально — прокрутка
    const min = Math.max(el.clientWidth / layout.w, el.clientHeight / layout.h);
    const c = cam.current;
    c.zoom = Math.max(min, Math.min(1.2, c.zoom));
    const halfW = el.clientWidth / c.zoom / 2;
    const halfH = el.clientHeight / c.zoom / 2;
    c.x = Math.max(halfW, Math.min(layout.w - halfW, c.x));
    c.y = Math.max(halfH, Math.min(layout.h - halfH, c.y));
  };

  const toScreen = (x: number, y: number) => {
    const el = wrap.current!;
    const c = cam.current;
    return { x: (x - c.x) * c.zoom + el.clientWidth / 2, y: (y - c.y) * c.zoom + el.clientHeight / 2 };
  };
  const toScene = (sx: number, sy: number) => {
    const el = wrap.current!;
    const c = cam.current;
    return { x: (sx - el.clientWidth / 2) / c.zoom + c.x, y: (sy - el.clientHeight / 2) / c.zoom + c.y };
  };

  const draw = (time: number) => {
    const cv = canvas.current;
    const el = wrap.current;
    if (!cv || !el) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const W = el.clientWidth;
    const H = el.clientHeight;
    if (cv.width !== Math.round(W * dpr)) cv.width = Math.round(W * dpr);
    if (cv.height !== Math.round(H * dpr)) cv.height = Math.round(H * dpr);
    const ctx = cv.getContext('2d')!;
    const c = cam.current;
    const onLoad = () => {
      dirty.current = true;
    };
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#0d2233';
    ctx.fillRect(0, 0, W, H);
    ctx.setTransform(
      dpr * c.zoom,
      0,
      0,
      dpr * c.zoom,
      dpr * (W / 2 - c.x * c.zoom),
      dpr * (H / 2 - c.y * c.zoom),
    );
    ctx.imageSmoothingQuality = 'high';
    const bg = image(url('background.webp'), onLoad);
    if (bg.complete && bg.naturalWidth) ctx.drawImage(bg, 0, 0, layout.w, layout.h);
    const tier = layout.wall.tiers[wallTier(wallLevel)];
    const blit = (key: string | null | undefined, pulse = false, dx = 0, dy = 0) => {
      if (!key) return;
      const sp = layout.sprites[key];
      if (!sp) return;
      const im = image(url(sp.file), onLoad);
      if (!im.complete || !im.naturalWidth) return;
      if (pulse) {
        ctx.save();
        ctx.shadowColor = 'rgba(255,214,96,.95)';
        ctx.shadowBlur = 18;
        ctx.drawImage(im, sp.x + dx, sp.y + dy, sp.w, sp.h);
        ctx.restore();
      }
      ctx.drawImage(im, sp.x + dx, sp.y + dy, sp.w, sp.h);
    };
    // Леса стройки: сдвиг с участка-образца на нужный участок
    const scaffoldAt = (type: string, lot: { x: number; y: number }, pulse: boolean) => {
      const a = layout.scaffolds?.anchor ?? [layout.lots.town_hall?.x ?? lot.x, layout.lots.town_hall?.y ?? lot.y];
      blit(scaffoldFor(type), pulse, lot.x - (a[0] ?? 0), lot.y - (a[1] ?? 0));
    };
    blit(tier?.back);
    // Здания по глубине (сверху вниз); прохожие встают между ними по своей глубине
    const lots = Object.entries(layout.lots).sort((a, b) => a[1].depth - b[1].depth);
    const people = walkers
      .map((w) => ({ w, y: walkerAt(w, time).y }))
      .sort((a, b) => a.y - b.y);
    let pi = 0;
    const shown: string[] = [];
    for (const [type, lot] of lots) {
      while (pi < people.length && people[pi]!.y < lot.depth) drawWalker(ctx, people[pi++]!.w, time);
      const b = byType.get(type);
      const sel =
        selected?.kind === 'building'
          ? selected.building.type === type
          : selected?.kind === 'plot' && plotType(selected) === type;
      if (!b || (b.level <= 0 && b.upgradingTo != null)) {
        // Пустой участок или первая стройка: леса
        if (b?.upgradingTo != null) {
          scaffoldAt(type, lot, sel);
          drawConstruction(ctx, lot.x, lot.y, time, workProgress(b));
        }
        continue;
      }
      const key = `b_${type}_s${cityStage(b.level)}`;
      blit(key, sel);
      shown.push(key);
      if (b.upgradingTo != null) drawConstruction(ctx, lot.x, lot.y, time, workProgress(b));
      const up = levelUps.current.get(type);
      if (up != null) drawLevelUp(ctx, lot.x, lot.y, time - up);
    }
    while (pi < people.length) drawWalker(ctx, people[pi++]!.w, time);
    blit(tier?.front);
    // Время суток по Москве (как на карте): ночью темнее, факелы ярче
    const light = daylightAt(gameHour());
    const warm = light.day > 0.05 && light.elevation < 14 ? 1 - Math.max(0, light.elevation) / 14 : 0;
    drawNight(ctx, layout.w, layout.h, light.day, warm);
    const power = 1 + 1.6 * (1 - light.day);
    // Огни и дым поверх слоёв
    drawTorches(ctx, layout.fx?.torch, time, 0, power);
    drawTorches(ctx, tier?.fx_back?.torch, time, 100, power);
    drawTorches(ctx, tier?.fx_front?.torch, time, 200, power);
    shown.forEach((key, i) => {
      const fx = layout.sprites[key]?.fx;
      drawTorches(ctx, fx?.torch, time, 300 + i * 7, power);
      drawSmoke(ctx, fx?.smoke, time);
    });
    if (light.day > 0.3) drawGulls(ctx, layout.w, layout.h, time);
    // Подписи «Название · уровень»
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.font = '600 12px system-ui, sans-serif';
    ctx.textAlign = 'center';
    for (const [type, lot] of lots) {
      const b = byType.get(type);
      const p = toScreen(lot.x, lot.y);
      if (p.x < -80 || p.y < -40 || p.x > W + 80 || p.y > H + 40) continue;
      const name = buildingLabels[type] ?? type;
      const text = b && b.level > 0 ? `${name} · ${b.level}` : `+ ${name}`;
      const tw = ctx.measureText(text).width + 14;
      ctx.fillStyle = b && b.level > 0 ? 'rgba(10,20,34,.82)' : 'rgba(120,84,14,.85)';
      ctx.beginPath();
      ctx.roundRect(p.x - tw / 2, p.y + 6, tw, 20, 6);
      ctx.fill();
      ctx.strokeStyle = 'rgba(214,176,92,.7)';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = '#f3e3b8';
      ctx.fillText(text, p.x, p.y + 20);
      if (guide === type) {
        const k = 0.5 + 0.5 * Math.sin(time * 5);
        ctx.fillStyle = `rgba(255,214,96,${0.5 + 0.5 * k})`;
        ctx.font = '700 26px system-ui';
        ctx.fillText('👇', p.x, p.y - 40 - k * 8);
        ctx.font = '600 12px system-ui, sans-serif';
      }
    }
  };

  const scaffoldFor = (type: string) =>
    ['town_hall', 'barracks', 'market', 'academy', 'headquarters', 'port', 'shipyard'].includes(type)
      ? 'scaffold_large'
      : 'scaffold_small';

  const plotType = (t: TownTarget & { kind: 'plot' }) => {
    const z = (t as CityTarget).zone;
    return z === 'field' ? FIELD_PLOT_TYPES[t.plot] : CASTLE_PLOT_TYPES[t.plot];
  };

  /** Доля готовности стройки/улучшения. */
  function workProgress(b: BuildingView): number {
    const start = b.upgradeStartAt ? new Date(b.upgradeStartAt).getTime() : 0;
    const end = b.upgradeFinishAt ? new Date(b.upgradeFinishAt).getTime() : 0;
    return end > start ? Math.max(0, Math.min(1, (Date.now() - start) / (end - start))) : 0.5;
  }

  const hit = (sx: number, sy: number): CityTarget | null => {
    const p = toScene(sx, sy);
    const lots = Object.entries(layout.lots).sort((a, b) => b[1].depth - a[1].depth);
    for (const [type, lot] of lots) {
      if (!inPolygon(lot.hit, p.x, p.y)) continue;
      const b = byType.get(type);
      if (b) return { kind: 'building', building: b, zone: b.zone === 'field' ? 'field' : 'castle' };
      const ci = (CASTLE_PLOT_TYPES as readonly string[]).indexOf(type);
      if (ci >= 0) return { kind: 'plot', plot: ci, zone: 'castle' };
      const fi = (FIELD_PLOT_TYPES as readonly string[]).indexOf(type);
      if (fi >= 0) return { kind: 'plot', plot: fi, zone: 'field' };
    }
    // Стена — по воротам
    for (const g of Object.values(layout.gates ?? {}))
      if (Math.hypot(p.x - (g[0] ?? 0), p.y - (g[1] ?? 0)) < 60) return { kind: 'wall' };
    return null;
  };

  // Перетаскивание и щипок
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef({ moved: false, pinch: 0 });
  return (
    <div
      ref={wrap}
      className="absolute inset-0 touch-none overflow-hidden"
      role="application"
      aria-label={label}
      onPointerDown={(e) => {
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        gesture.current = { moved: false, pinch: 0 };
      }}
      onPointerMove={(e) => {
        const prev = pointers.current.get(e.pointerId);
        if (!prev) return;
        const c = cam.current;
        if (pointers.current.size === 2) {
          pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
          const [a, b] = [...pointers.current.values()] as [
            { x: number; y: number },
            { x: number; y: number },
          ];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (gesture.current.pinch) c.zoom *= d / gesture.current.pinch;
          gesture.current.pinch = d;
          gesture.current.moved = true;
        } else {
          const dx = e.clientX - prev.x;
          const dy = e.clientY - prev.y;
          if (Math.abs(dx) + Math.abs(dy) > 3) gesture.current.moved = true;
          c.x -= dx / c.zoom;
          c.y -= dy / c.zoom;
          pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        }
        fit();
        dirty.current = true;
      }}
      onPointerUp={(e) => {
        pointers.current.delete(e.pointerId);
        if (pointers.current.size < 2) gesture.current.pinch = 0;
        if (!gesture.current.moved && pointers.current.size === 0) {
          const r = wrap.current!.getBoundingClientRect();
          onSelect(hit(e.clientX - r.left, e.clientY - r.top));
          redraw((v) => v + 1);
        }
      }}
      onWheel={(e) => {
        cam.current.zoom *= e.deltaY < 0 ? 1.1 : 1 / 1.1;
        fit();
        dirty.current = true;
      }}
    >
      <canvas ref={canvas} className="block h-full w-full" />
    </div>
  );
}
